import { useEffect } from 'react';

interface SEOMeta {
  name?: string;
  property?: string;
  content: string;
}

interface PageMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogLocale?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  jsonLdScripts?: Record<string, unknown>[];
}

export default function PageMeta({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  ogLocale = 'zh_CN',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  jsonLdScripts = [],
}: PageMetaProps) {
  useEffect(() => {
    document.title = title;

    const metaTags: SEOMeta[] = [
      { name: 'description', content: description },
    ];

    if (keywords) {
      metaTags.push({ name: 'keywords', content: keywords });
    }

    if (ogTitle) {
      metaTags.push({ property: 'og:title', content: ogTitle });
    }
    if (ogDescription) {
      metaTags.push({ property: 'og:description', content: ogDescription });
    }
    metaTags.push({ property: 'og:type', content: ogType });
    if (ogUrl) {
      metaTags.push({ property: 'og:url', content: ogUrl });
    }
    metaTags.push({ property: 'og:locale', content: ogLocale });

    if (twitterCard) {
      metaTags.push({ name: 'twitter:card', content: twitterCard });
    }
    if (twitterTitle) {
      metaTags.push({ name: 'twitter:title', content: twitterTitle });
    }
    if (twitterDescription) {
      metaTags.push({ name: 'twitter:description', content: twitterDescription });
    }

    const createdElements: HTMLElement[] = [];

    metaTags.forEach(({ name, property, content }) => {
      let element: HTMLElement | null = null;
      if (name) {
        element = document.querySelector(`meta[name="${name}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('name', name);
          document.head.appendChild(element);
          createdElements.push(element);
        }
      } else if (property) {
        element = document.querySelector(`meta[property="${property}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('property', property);
          document.head.appendChild(element);
          createdElements.push(element);
        }
      }
      if (element) {
        element.setAttribute('content', content);
      }
    });

    if (canonical) {
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
        createdElements.push(linkEl);
      }
      linkEl.setAttribute('href', canonical);
    }

    const scriptIds: string[] = [];
    jsonLdScripts.forEach((jsonLd, index) => {
      const scriptId = `json-ld-${index}`;
      scriptIds.push(scriptId);
      let scriptEl = document.getElementById(scriptId);
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.setAttribute('type', 'application/ld+json');
        scriptEl.id = scriptId;
        document.head.appendChild(scriptEl);
        createdElements.push(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    });

    return () => {
      createdElements.forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, []);

  return null;
}